import { useEffect, useRef, useState, useCallback } from "react";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";

export default function useVoteWebSocket(sessionId) {
  const [messages, setMessages] = useState([]);
  const stompClientRef = useRef(null);
  const isConnectedRef = useRef(false);
  const reconnectLock = useRef(false);

  const connect = useCallback(() => {
    const client = new Client({
      webSocketFactory: () => new SockJS(`${process.env.REACT_APP_API_URL}/ws`),
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        console.log("Vote WebSocket connected");
        isConnectedRef.current = true;
        reconnectLock.current = false;

        client.subscribe(`/topic/sessions/${sessionId}`, (msg) => {
          try {
            const data = JSON.parse(msg.body);
            setMessages((prev) => [...prev, data]);
          } catch (e) {
            console.error("Failed to parse Vote WS message:", e);
          }
        });
      },
      onStompError: (frame) => console.error("Vote WS STOMP error:", frame),
      onDisconnect: () => (isConnectedRef.current = false),
    });

    client.activate();
    stompClientRef.current = client;
    return client;
  }, [sessionId]);

  const tryReconnect = useCallback(() => {
    if (stompClientRef.current?.connected || reconnectLock.current) return;
    reconnectLock.current = true;
    stompClientRef.current?.deactivate();
    connect();
    reconnectLock.current = false;
  }, [connect]);

  useEffect(() => {
    if (!sessionId) return;

    connect();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") tryReconnect();
    };

    const interval = setInterval(() => tryReconnect(), 30000);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stompClientRef.current?.deactivate();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sessionId, connect, tryReconnect]);

  const sendVote = async (topicId, voteType = null, username = null) => {
    if (!stompClientRef.current?.connected) {
      await new Promise((resolve) => {
        const tempClient = connect();
        const check = setInterval(() => {
          if (tempClient.connected) {
            clearInterval(check);
            resolve(true);
          }
        }, 200);
      });
    }

    if (stompClientRef.current?.connected) {
      stompClientRef.current.publish({
        destination: `/app/vote/${sessionId}`,
        body: `${topicId}`,
        headers: { voteType: voteType || '', voterUsername: username || '' },
      });
    } else {
      console.warn("Failed to send vote, still disconnected");
    }
  };

  return { messages, sendVote };
}
